/**
 * Environment configuration module
 * Centralizes all environment-based configuration
 */

interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;
  wsBaseUrl: string;
  useApiGateway: boolean;

  // Feature Flags
  enableWebSocket: boolean;
  enableDevAuth: boolean; // Enables credential login + auth debug panel
  enableFeedback: boolean; // Enables thumbs up/down on assistant messages (Spec 014)

  // Environment Info
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
  environment: 'development' | 'staging' | 'production';
}

/**
 * Get the current environment based on various indicators
 */
const getCurrentEnvironment = (): 'development' | 'staging' | 'production' => {
  // Check Vite's mode first
  if (import.meta.env.MODE === 'staging') return 'staging';
  if (import.meta.env.MODE === 'production') return 'production';
  
  // Check if running in production build
  if (import.meta.env.PROD) {
    // Additional check for staging URL patterns
    const hostname = window.location.hostname;
    if (hostname.includes('staging') || hostname.includes('stg')) {
      return 'staging';
    }
    return 'production';
  }
  
  return 'development';
};

/**
 * Parse boolean environment variables
 */
const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true';
};

/**
 * Environment configuration
 */
export const environment: EnvironmentConfig = {
  // Determine environment
  environment: getCurrentEnvironment(),
  isDevelopment: getCurrentEnvironment() === 'development',
  isStaging: getCurrentEnvironment() === 'staging',
  isProduction: getCurrentEnvironment() === 'production',
  
  // API Configuration - imported from api.ts to avoid duplication
  apiBaseUrl: '', // Set by api.ts
  wsBaseUrl: '', // Set by api.ts
  
  // Feature flags
  useApiGateway: parseBoolean(import.meta.env.VITE_USE_API_GATEWAY, false),
  enableWebSocket: parseBoolean(import.meta.env.VITE_ENABLE_WEBSOCKET, true),
  enableDevAuth: parseBoolean(import.meta.env.VITE_ENABLE_DEV_AUTH, false),
  enableFeedback: parseBoolean(import.meta.env.VITE_ENABLE_FEEDBACK, false),
};

/**
 * API endpoint configuration
 * Maps logical endpoint names to actual paths
 */
export const apiEndpoints = {
  // Stakeholder endpoints
  stakeholders: {
    list: '/stakeholders',
    get: (id: string) => `/stakeholders/${id}`,
    search: '/stakeholders/search',
  },
  
  // Conversation endpoints
  conversations: {
    list: '/conversations',
    create: '/conversations',
    get: (id: string) => `/conversations/${id}`,
    messages: (id: string) => `/conversations/${id}/messages`,
    sendMessage: (id: string) => `/conversations/${id}/messages`,
    complete: (id: string) => `/conversations/${id}/complete`,
  },
  
  // Assessment endpoints
  assessments: {
    list: '/assessments',
    get: (id: string) => `/assessments/${id}`,
    create: '/assessments',
    update: (id: string) => `/assessments/${id}`,
    submit: (id: string) => `/assessments/${id}/submit`,
  },
  
  // Artifact endpoints
  artifacts: {
    list: '/artifacts',
    get: (id: string) => `/artifacts/${id}`,
    create: '/artifacts',
    update: (id: string) => `/artifacts/${id}`,
    delete: (id: string) => `/artifacts/${id}`,
    download: (id: string) => `/artifacts/${id}/download`,
  },
  
  // Auth endpoints
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile',
    updateProfile: '/auth/profile',
  },
  
  // Tenant endpoints
  tenants: {
    current: '/tenants/current',
    list: '/tenants',
    get: (id: string) => `/tenants/${id}`,
  },
};

/**
 * Get full URL for an endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  // This will be set by api.ts after initialization
  const baseUrl = environment.apiBaseUrl || import.meta.env.VITE_API_BASE_URL;
  return `${baseUrl}${endpoint}`;
};

/**
 * Check if API Gateway is enabled
 */
export const isApiGatewayEnabled = (): boolean => {
  return environment.useApiGateway && !!import.meta.env.VITE_API_GATEWAY_URL;
};

/**
 * Log current environment configuration (for debugging)
 */
export const logEnvironmentConfig = (): void => {
  if (environment.isDevelopment) {
    console.log('Environment Configuration:', {
      environment: environment.environment,
      apiBaseUrl: environment.apiBaseUrl || 'Not set',
      wsBaseUrl: environment.wsBaseUrl || 'Not set',
      useApiGateway: environment.useApiGateway,
      enableWebSocket: environment.enableWebSocket,
      apiGatewayUrl: import.meta.env.VITE_API_GATEWAY_URL || 'Not configured',
    });
  }
};

export default environment;